import React, { useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import {
  Squares2X2Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  EyeIcon,
  UserCircleIcon,
  ArrowPathRoundedSquareIcon
} from "@heroicons/react/24/outline";
import { Breadcrumb } from "../../../../components/Breadcrumb";
import httpClient, { TimeoutError } from "../../../../utils/httpClient";
import { StatusFilterState } from "./types";
import { useAdminApplications, useProjects } from "../../../../hooks/useBonusQueries";
import { getPreviousQuarter } from "../../../../utils/dateUtils";
import { BonusApplication, BonusApplicationHistoryItem } from "../../../../services/bonus/types";
import BonusApplicationHistory from "../../../../components/SubForms/Common/BonusApplicationHistory";
import CommentComponent from "../../../../components/SubForms/Common/CommentComponent";
import ViewApplicationDialog from "./ViewApplicationDialog";
import UpdateReviewerDialog from "./UpdateReviewerDialog";
import UpdateStatusDialog from "./UpdateStatusDialog";

const STORAGE_KEY = "adminApplicationsFilters";

// Status color mapping - FIXED: Use actual database status values
const statusColors: Record<string, { bg: string; text: string }> = {
  'Draft': { bg: 'bg-slate-100', text: 'text-slate-700' },
  'Rejected': { bg: 'bg-red-100', text: 'text-red-700' },
  'Cancelled': { bg: 'bg-gray-100', text: 'text-gray-700' },
  'Submitted': { bg: 'bg-blue-100', text: 'text-blue-700' },
  'Bonus_Recipient_Review': { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  'SPM_Review': { bg: 'bg-sky-100', text: 'text-sky-700' },
  'DCCM_Review': { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  'DM_Review': { bg: 'bg-teal-100', text: 'text-teal-700' },
  'VP_Review': { bg: 'bg-amber-100', text: 'text-amber-700' },
  'Ops_Review': { bg: 'bg-orange-100', text: 'text-orange-700' },
  'Approved': { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  'Payroll_Processing': { bg: 'bg-violet-100', text: 'text-violet-700' },
  'Paid': { bg: 'bg-green-100', text: 'text-green-700' },
};

const Applications: React.FC = () => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [canUpdateApplications, setCanUpdateApplications] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>(
    localStorage.getItem("selectedRole") || ""
  );

  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<BonusApplication | null>(null);
  const [reviewerDialogOpen, setReviewerDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);


  const prevQuarter = getPreviousQuarter();

  const getInitialFilters = (): StatusFilterState => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.year < prevQuarter.year ||
          (parsed.year === prevQuarter.year && parsed.quarter <= prevQuarter.quarter)) {
          const { applicant: legacyApplicant, ...rest } = parsed;
          return {
            ...rest,
            bonusRecipient: parsed.bonusRecipient ?? legacyApplicant ?? "",
          } as StatusFilterState;
        }
      } catch (e) {
        console.error("Failed to parse stored filters:", e);
      }
    }

    return {
      applicationType: "All",
      status: "All",
      bonusRecipient: "",
      region: "All",
      resultType: "json",
      year: prevQuarter.year,
      quarter: prevQuarter.quarter,
      page: 1,
      pageSize: 25,
    };
  };

  const [filters, setFilters] = useState<StatusFilterState>(getInitialFilters());

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  // Use hooks for data fetching
  const { data: apiResponse, isLoading } = useAdminApplications(filters);
  const { data: projectsData } = useProjects();

  // Extract canUpdateApplications from API response headers
  useEffect(() => {
    if (apiResponse) {
      // TODO: Extract from response headers when API is updated
      // For now, using role-based logic
      console.log("API Response received, determining update permissions.");
      console.log("User role:", selectedRole);
      const authorizedRoles = ['Admin', 'Ops'];
      setCanUpdateApplications(authorizedRoles.some(r => selectedRole?.includes(r)));
    }
  }, [apiResponse, selectedRole]);

  const applications = apiResponse?.data || [];
  const totalCount = apiResponse?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / filters.pageSize);

  // Create project lookup map
  const projectMap = React.useMemo(() => {
    if (!projectsData) return new Map();
    return new Map(
      projectsData.map(p => [p.projectNumber, p.projectName])
    );
  }, [projectsData]);

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleClearFilters = () => {
    const defaultFilters: StatusFilterState = {
      applicationType: "All",
      status: "All",
      bonusRecipient: "",
      region: "All",
      resultType: "json",
      year: prevQuarter.year,
      quarter: prevQuarter.quarter,
      page: 1,
      pageSize: 25,
    };
    setFilters(defaultFilters);
    localStorage.removeItem(STORAGE_KEY);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  const breadcrumbPages = [
    {
      name: "Admin",
      href: "#",
      current: false,
    },
    {
      name: "Manage",
      href: "#",
      current: false,
    },
    {
      name: "Applications",
      href: "/admin/manage/applications",
      current: true,
      icon: (props: React.ComponentProps<"svg">) => (
        <Squares2X2Icon
          {...props}
          className="w-6 h-6 text-primary-500 transition duration-75 group-hover:text-gray-900"
        />
      ),
    },
  ];

  const handleFilterChange = (field: keyof StatusFilterState, value: string | number) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 1,
    }));
  };

  const formatDateTime = (): string => {
    const now = new Date();
    const centralTime = now.toLocaleString('sv-SE', {
      timeZone: 'America/Chicago',
      hour12: false
    });
    return centralTime?.replace(/[-: ]/g, '')?.replace(/(\d{8})(\d{6})/, '$1_$2') || '';
  };

  const handleExportToExcel = async () => {
    setIsExporting(true);
    const formattedDate = formatDateTime();
    const filename = `QBA_Applications_${filters.year}_${filters.quarter}_${formattedDate}.xlsx`;

    try {
      const response = await httpClient.get("/api/manage/applications", {
        params: {
          year: filters.year,
          quarter: filters.quarter,
          applicationType: filters.applicationType,
          status: filters.status,
          BonusRecipient: filters.bonusRecipient,
          region: filters.region,
          resultType: "excel",
        },
        responseType: 'blob',
        headers: { roleType: selectedRole || "" },
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: unknown) {
      if (error instanceof TimeoutError) {
        console.error(`Request timed out for ${(error as TimeoutError).config.method.toUpperCase()} ${(error as TimeoutError).config.url} — ${(error as TimeoutError).message}. Please check if the export went through before trying again`);
      } else {
        console.error("Error exporting to Excel:", error);
      }
    } finally {
      setIsExporting(false);
    }
  };

  const toggleRowExpansion = (id: number | undefined) => {
    if (id === undefined || id === null) {
      console.error('Invalid ID passed to toggleRowExpansion');
      return;
    }

    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  const getMaxQuarter = (year: number): number => {
    if (year < prevQuarter.year) return 4;
    if (year === prevQuarter.year) return prevQuarter.quarter;
    return 1;
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime2 = (date: Date | string | null) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' +
      d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const getStatusColor = (status: string | undefined) => {
    if (!status) return { bg: 'bg-gray-100', text: 'text-gray-700' };

    // Try exact match first
    if (statusColors[status]) {
      return statusColors[status];
    }

    // Try with underscores replaced
    const withUnderscore = status?.replace(/ /g, '_') || '';
    if (statusColors[withUnderscore]) {
      return statusColors[withUnderscore];
    }

    // Default
    return { bg: 'bg-gray-100', text: 'text-gray-700' };
  };

  const getApplicationCategory = (type: string) => {
    const constructionTypes = ['CM3', 'PM', 'SPM'];
    const customerCareTypes = ['CCM1', 'CCM2', 'CCM3', 'CCM3Manager', 'DCCM'];

    if (constructionTypes.includes(type)) return 'Construction';
    if (customerCareTypes.includes(type)) return 'Customer Care';
    return '';
  };

  const getProjectDetails = (projectNumbers: string | null) => {
    if (!projectNumbers) return null;
    const numbers = projectNumbers.split(',').map(n => n.trim());
    return numbers
      .map(num => {
        const projectName = projectMap.get(num);
        return projectName ? `${projectName} [${num}]` : `Unknown [${num}]`;
      })
      .join(' | ');
  };

  const getReviewHistory = (app: BonusApplication) => {
    if (filters.year >= 2026) {
      return [
        { stage: 'Created', completedBy: app.createdByName || app.createdBy, completedOn: app.createdOn },
        { stage: 'Draft', completedBy: app.submittedByName || app.submittedBy, completedOn: null },
        { stage: 'Submitted', completedBy: app.submittedByName || app.submittedBy, completedOn: app.submittedOn },
        { stage: 'Bonus Recipient Review', completedBy: app.bonusRecipientName || app.bonusRecipientEmail, completedOn: app.bonusRecipientReviewedOn },
        { stage: 'VP Review', completedBy: app.vpReviewedByName || app.vpReviewedBy, completedOn: app.vpReviewedOn },
        { stage: 'Ops Review', completedBy: app.opsReviewedByName || app.opsReviewedBy, completedOn: app.opsReviewedOn },
        { stage: 'Approved', completedBy: null, completedOn: null },
        { stage: 'Payroll Processing', completedBy: app.payrollProcessedByName || app.payrollProcessedBy, completedOn: app.payrollProcessingOn },
        { stage: 'Paid', completedBy: null, completedOn: app.paymentDate },
      ].filter(h => h.completedBy || h.completedOn);
    }

    const history = [
      { stage: 'Created', completedBy: app.createdByName || app.createdBy, completedOn: app.createdOn },
      { stage: 'Draft', completedBy: app.submittedByName || app.submittedBy, completedOn: null },
      { stage: 'Submitted', completedBy: app.submittedByName || app.submittedBy, completedOn: app.submittedOn },
      { stage: 'DCCM Review', completedBy: app.dccmReviewedByName || app.dccmReviewedBy, completedOn: app.dccmReviewedOn },
      { stage: 'VP Review', completedBy: app.vpReviewedByName || app.vpReviewedBy, completedOn: app.vpReviewedOn },
      { stage: 'Ops Review', completedBy: app.opsReviewedByName || app.opsReviewedBy, completedOn: app.opsReviewedOn },
      { stage: 'Approved', completedBy: null, completedOn: null },
      { stage: 'Payroll Processing', completedBy: app.payrollProcessedByName || app.payrollProcessedBy, completedOn: app.payrollProcessingOn },
      { stage: 'Paid', completedBy: null, completedOn: app.paymentDate },
    ];

    if (app.applicationType === 'SPM' || app.applicationType === 'PM' || app.applicationType === 'CM3') {
      history.splice(3, 0,
        { stage: 'SPM Review', completedBy: app.spmReviewedByName || app.spmReviewedBy, completedOn: app.spmReviewedOn },
        { stage: 'DM Review', completedBy: app.dmReviewedByName || app.dmReviewedBy, completedOn: app.dmReviewedOn }
      );
    }

    return history.filter(h => h.completedBy || h.completedOn);
  };

  const convertToHistoryItems = (app: BonusApplication): BonusApplicationHistoryItem[] => {
    const reviewHistory = getReviewHistory(app);

    return reviewHistory.map((item, index): BonusApplicationHistoryItem => ({
      historyId: index + 1,
      bonusApplicationId: app.applicationId,
      statusId: 0,
      previousStatusId: null,
      currentStatusName: item.stage,
      previousStatusName: null,
      currentStatusDescription: null,
      previousStatusDescription: null,
      changedBy: item.completedBy || null,
      changedOn: item.completedOn ? new Date(item.completedOn).toISOString() : new Date().toISOString(),
      completedByEmail: item.completedBy || null,
      completedOn: item.completedOn ? new Date(item.completedOn).toISOString() : new Date().toISOString(),
      comment: null,
      isBulkAction: false,
      bulkActionPerformedBy: null,
      bulkActionPerformedOn: null,
      statusOccurrenceNumber: 1,
      isLatestForStatus: true,
      createdBy: item.completedBy || null,
      createdOn: item.completedOn ? new Date(item.completedOn).toISOString() : new Date().toISOString(),
      updatedBy: null,
      updatedOn: null,
      changeType: "",
      amount: null,
      year: 0,
      quarter: 0,
      requestDate: null,
      paymentReference: null,
      originalCreatedOn: null,
      originalCreatedBy: null,
      originalUpdatedOn: null,
      originalUpdatedBy: null
    }));
  };

  // View Details Handler
  const handleViewDetails = (app: BonusApplication) => {
    setSelectedApplication(app);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedApplication(null);
  };

  // Update Reviewer Handlers
  const handleOpenReviewerDialog = (app: BonusApplication) => {
    setSelectedApplication(app);
    setReviewerDialogOpen(true);
  };

  const handleCloseReviewerDialog = () => {
    setReviewerDialogOpen(false);
    setSelectedApplication(null);
  };

  // Update Status Handlers
  const handleOpenStatusDialog = (app: BonusApplication) => {
    setSelectedApplication(app);
    setStatusDialogOpen(true);
  };

  const handleCloseStatusDialog = () => {
    setStatusDialogOpen(false);
    setSelectedApplication(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="xl:pl-0 flex-grow flex flex-col">
        <main className="flex-grow flex flex-col overflow-hidden">
          <div className="relative isolate overflow-hidden pt-6 flex-grow flex flex-col">
            <div className="mx-auto w-full px-4 sm:px-6 lg:px-8">
              <div className="mb-6">
                <Breadcrumb pages={breadcrumbPages} />
              </div>

              {/* Search Filters */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Search Filters
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Year */}
                    <div>
                      <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1">
                        Year
                      </label>
                      <select
                        id="year"
                        value={filters.year}
                        onChange={(e) => {
                          const newYear = parseInt(e.target.value);
                          const maxQuarter = getMaxQuarter(newYear);
                          handleFilterChange("year", newYear);
                          if (filters.quarter > maxQuarter) {
                            handleFilterChange("quarter", maxQuarter);
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        {yearOptions.map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>

                    {/* Quarter */}
                    <div>
                      <label htmlFor="quarter" className="block text-sm font-medium text-gray-700 mb-1">
                        Quarter
                      </label>
                      <select
                        id="quarter"
                        value={filters.quarter}
                        onChange={(e) => handleFilterChange("quarter", parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        {[1, 2, 3, 4].map(q => (
                          <option key={q} value={q} disabled={q > getMaxQuarter(filters.year)}>
                            Q{q}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Application Type */}
                    <div>
                      <label htmlFor="applicationType" className="block text-sm font-medium text-gray-700 mb-1">
                        Application Type
                      </label>
                      <select
                        id="applicationType"
                        value={filters.applicationType}
                        onChange={(e) => handleFilterChange("applicationType", e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="All">All</option>
                        <optgroup label="Customer Care">
                          <option value="AllCustomerCare">All Customer Care</option>
                          <option value="CCM1">CCM1</option>
                          <option value="CCM2">CCM2</option>
                          <option value="CCM3">CCM3</option>
                          <option value="DCCM">DCCM</option>
                        </optgroup>
                        <optgroup label="Construction">
                          <option value="AllConstruction">All Construction</option>
                          <option value="CM3">CM3</option>
                          <option value="PM">PM</option>
                          <option value="SPM">SPM</option>
                        </optgroup>
                      </select>
                    </div>

                    {/* Status */}
                    <div>
                      <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        id="status"
                        value={filters.status}
                        onChange={(e) => handleFilterChange("status", e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="All">All</option>
                        <optgroup label="Applicant">
                          <option value="All-Applicant">All Applicant</option>
                          <option value="Draft">Draft</option>
                          <option value="Rejected">Rejected</option>
                          <option value="Cancelled">Cancelled</option>
                        </optgroup>
                        <optgroup label="Customer Care Review">
                          <option value="Customer-Care Review">All - Customer Care Review</option>
                          <option value="DCCM-Review">DCCM Review</option>
                          <option value="VP-Review">VP Review (CC)</option>
                        </optgroup>
                        <optgroup label="Construction Review">
                          <option value="Construction Review">All - Construction Review</option>
                          <option value="SPM-Review">SPM Review</option>
                          <option value="DM-Review">DM Review</option>
                          <option value="VP-Review">VP Review (Const)</option>
                        </optgroup>
                        <optgroup label="Operations">
                          <option value="Ops">Ops Review</option>
                        </optgroup>
                        <optgroup label="Payroll">
                          <option value="Payroll">All - Payroll</option>
                          <option value="Payroll Processing">Payroll Processing</option>
                          <option value="Paid">Paid</option>
                        </optgroup>
                      </select>
                    </div>

                    {/* Bonus Recipient */}
                    <div>
                      <label htmlFor="bonusRecipient" className="block text-sm font-medium text-gray-700 mb-1">
                        Bonus Recipient
                      </label>
                      <input
                        type="text"
                        id="bonusRecipient"
                        value={filters.bonusRecipient}
                        onChange={(e) => handleFilterChange("bonusRecipient", e.target.value)}
                        placeholder="Name or Email"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    {/* Region */}
                    <div>
                      <label htmlFor="region" className="block text-sm font-medium text-gray-700 mb-1">
                        Region
                      </label>
                      <select
                        id="region"
                        value={filters.region}
                        onChange={(e) => handleFilterChange("region", e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="All">All</option>
                        <optgroup label="Texas">
                          <option value="Austin">Austin</option>
                          <option value="DFW">DFW</option>
                          <option value="Houston">Houston</option>
                          <option value="San Antonio">San Antonio</option>
                        </optgroup>
                        <optgroup label="">
                          <option value="Other">Other</option>
                        </optgroup>
                      </select>
                    </div>

                    {/* Page Size */}
                    <div>
                      <label htmlFor="pageSize" className="block text-sm font-medium text-gray-700 mb-1">
                        Results per Page
                      </label>
                      <select
                        id="pageSize"
                        value={filters.pageSize}
                        onChange={(e) => handleFilterChange("pageSize", parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handleClearFilters}
                      className="w-full sm:w-auto px-6 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                    >
                      Clear
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full sm:w-auto px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isLoading ? "Searching..." : "Search"}
                    </button>
                  </div>
                </form>
              </div>

              {/* Results Section */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
                  <div className="flex items-center gap-4">
                    <h2 className="text-lg font-semibold text-gray-900">Results</h2>
                    <div className="text-sm text-gray-500">
                      {totalCount > 0 ? (
                        <>
                          <span className="hidden sm:inline">
                            Showing <span className="font-medium">{(filters.page - 1) * filters.pageSize + 1}</span>
                            {' '}to <span className="font-medium">{Math.min(filters.page * filters.pageSize, totalCount)}</span>
                            {' '}of{' '}
                          </span>
                          <span className="font-medium">{totalCount}</span>
                          {' '}application{totalCount !== 1 ? 's' : ''}
                        </>
                      ) : ('0 applications')}
                    </div>
                  </div>
                  {applications.length > 0 && (
                    <div className="relative group w-full sm:w-auto">
                      <button
                        onClick={handleExportToExcel}
                        disabled={isExporting}
                        className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {isExporting ? "Exporting..." : "Export to Excel"}
                      </button>
                      <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                        Exports all records matching the current search filters (not just the current page)
                        <div className="absolute top-full right-4 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  )}
                </div>

                {isLoading ? (
                  <div className="flex justify-center py-12">
                    <CircularProgress />
                  </div>
                ) : applications.length === 0 ? (
                  <div className="text-gray-500 text-center p-8 bg-gray-50 rounded-lg">
                    <Squares2X2Icon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-lg font-medium">No applications found</p>
                    <p className="text-sm">Try adjusting your search filters to find more results.</p>
                  </div>
                ) : (
                  <>
                    {/* Desktop Table View */}
                    <div className="hidden lg:block overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                              Actions
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Application Type
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Applicant [ID] Location
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Date Created
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Last Updated
                            </th>
                            {canUpdateApplications && (
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Amount
                              </th>
                            )}
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                              {/* Expand column */}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {applications.map((app: BonusApplication) => {
                            const projectDetails = getProjectDetails(app.projectNumbers);
                            const colors = getStatusColor(app.currentStatus);
                            const category = getApplicationCategory(app.applicationType);

                            return (
                              <React.Fragment key={app.applicationId}>
                                {/* Main Row */}
                                <tr className="hover:bg-gray-50">
                                  {/* Actions */}

                                  {/* Actions */}
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <div className="relative group/view">
                                        <button
                                          onClick={() => handleViewDetails(app)}
                                          className="text-gray-400 hover:text-primary-600 transition-colors"
                                        >
                                          <EyeIcon className="w-5 h-5" />
                                        </button>
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 pl-8 pr-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover/view:opacity-100 group-hover/view:visible transition-all z-50 pointer-events-none">
                                          View Details
                                        </div>
                                      </div>
                                      {canUpdateApplications && (
                                        <>
                                          <div className="relative group/reviewer">
                                            <button
                                              onClick={() => handleOpenReviewerDialog(app)}
                                              className="text-gray-400 hover:text-primary-600 transition-colors"
                                            >
                                              <UserCircleIcon className="w-5 h-5" />
                                            </button>
                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2  pl-8 pr-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover/reviewer:opacity-100 group-hover/reviewer:visible transition-all z-50 pointer-events-none">
                                              Update Reviewer
                                            </div>
                                          </div>
                                          <div className="relative group/status">
                                            <button
                                              onClick={() => handleOpenStatusDialog(app)}
                                              className="text-gray-400 hover:text-primary-600 transition-colors"
                                            >
                                              <ArrowPathRoundedSquareIcon className="w-5 h-5" />
                                            </button>
                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2  pl-8 pr-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover/status:opacity-100 group-hover/status:visible transition-all z-50 pointer-events-none">
                                              Update Status
                                            </div>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </td>

                                  {/* Application Type & Projects */}
                                  <td className="px-4 py-3 text-sm">
                                    <div className="space-y-1">
                                      <div className="font-semibold text-gray-900">
                                        <span className="text-gray-900">{app.applicationType}</span>
                                        {category && (
                                          <span className="text-gray-500 font-normal ml-1">· {category}</span>
                                        )}
                                      </div>
                                      {projectDetails && (
                                        <div className="text-xs text-gray-600 italic">
                                          {projectDetails}
                                        </div>
                                      )}
                                    </div>
                                  </td>

                                  {/* Status */}
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${colors.bg} ${colors.text}`}>
                                      {app.status ? app.status.replace(/_/g, ' ') : '-'}
                                    </span>
                                  </td>

                                  {/* Applicant & ID */}
                                  <td className="px-4 py-3 text-sm">
                                    <div className="font-medium text-gray-900">
                                      {app.employeeName || app.submittedBy}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      [{app.applicationId}] | {app.location}
                                    </div>
                                  </td>

                                  {/* Date Created */}
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                    {formatDate(app.createdOn)}
                                  </td>

                                  {/* Last Updated */}
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                    {formatDate(app.updatedOn)}
                                  </td>

                                  {/* Amount (conditional) */}
                                  {canUpdateApplications && (
                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                      {app.amount ? `$${app.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '-'}
                                    </td>
                                  )}

                                  {/* Expand Icon */}
                                  <td className="px-4 py-3 whitespace-nowrap text-right">
                                    <button
                                      onClick={() => toggleRowExpansion(app.applicationId)}
                                      className="text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                      {expandedRows.has(app.applicationId) ? (
                                        <ChevronUpIcon className="w-5 h-5" />
                                      ) : (
                                        <ChevronDownIcon className="w-5 h-5" />
                                      )}
                                    </button>
                                  </td>
                                </tr>

                                {expandedRows.has(app.applicationId) && (
                                  <tr className="bg-gray-50">
                                    <td colSpan={canUpdateApplications ? 8 : 7} className="px-4 py-6">
                                      {/* Side-by-Side Layout: History with inline comments | Standalone comments */}
                                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                        {/* Left Column: History with inline comments */}
                                        <div>
                                          <BonusApplicationHistory
                                            historyItems={convertToHistoryItems(app)}
                                            comments={app.comments || []}
                                            historyAccordionExpanded={false}
                                            onAccordionToggle={() => { }}
                                            showAccordion={false}
                                            showTitle={true}
                                          />
                                        </div>

                                        {/* Right Column: Standalone comments (not tied to history items) */}
                                        <div>
                                          <CommentComponent
                                            comment=""
                                            commentList={app.comments || []}
                                            commentsAccordionExpanded={false}
                                            onCommentChange={() => { }}
                                            onAddComment={() => { }}
                                            onAccordionToggle={() => { }}
                                            showAccordion={false}
                                            showTitle={true}
                                            readOnly={true}
                                            filterByHistoryId={null}
                                          />
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile/Tablet Card View */}
                    <div className="lg:hidden space-y-4">
                      {applications.map((app: BonusApplication) => {
                        const projectDetails = getProjectDetails(app.projectNumbers);
                        const colors = getStatusColor(app.currentStatus);
                        const category = getApplicationCategory(app.applicationType);

                        return (
                          <div key={app.applicationId} className="bg-white border border-gray-200 rounded-lg p-4">
                            {/* Header */}
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="font-semibold text-gray-900">
                                  {app.applicationType}
                                  {category && <span className="text-gray-500 font-normal text-sm ml-1">· {category}</span>}
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                  {app.employeeName || app.submittedBy}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  ID: {app.applicationId} | {app.location}
                                </div>
                              </div>
                              <span className={`px-2.5 py-1 text-xs leading-5 font-semibold rounded-full ${colors.bg} ${colors.text}`}>
                                {app.currentStatus ? app.currentStatus.replace(/_/g, ' ') : 'N/A'}
                              </span>
                            </div>

                            {/* Project Details */}
                            {projectDetails && (
                              <div className="text-xs text-gray-600 italic mb-3 pb-3 border-b border-gray-200">
                                {projectDetails}
                              </div>
                            )}

                            {/* Dates */}
                            <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                              <div>
                                <span className="text-gray-500">Created:</span>
                                <div className="text-gray-900">{formatDate(app.createdOn)}</div>
                              </div>
                              <div>
                                <span className="text-gray-500">Updated:</span>
                                <div className="text-gray-900">{formatDate(app.updatedOn)}</div>
                              </div>
                            </div>

                            {/* Amount */}
                            {canUpdateApplications && app.amount && (
                              <div className="text-sm font-semibold text-gray-900 mb-3">
                                Amount: ${app.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                              </div>
                            )}

                            {/* Actions */}
                            <div className="flex items-center gap-3 pt-3 border-t border-gray-200">
                              <button
                                onClick={() => handleViewDetails(app)}
                                className="flex-1 py-2 px-3 bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                              >
                                <EyeIcon className="w-4 h-4" />
                                View
                              </button>
                              {canUpdateApplications && (
                                <>
                                  <button
                                    onClick={() => handleOpenReviewerDialog(app)}
                                    className="flex-1 py-2 px-3 bg-gray-50 text-gray-700 rounded-md hover:bg-gray-100 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                                  >
                                    <UserCircleIcon className="w-4 h-4" />
                                    Reviewer
                                  </button>
                                  <button
                                    onClick={() => handleOpenStatusDialog(app)}
                                    className="flex-1 py-2 px-3 bg-gray-50 text-gray-700 rounded-md hover:bg-gray-100 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                                  >
                                    <ArrowPathRoundedSquareIcon className="w-4 h-4" />
                                    Status
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => toggleRowExpansion(app.applicationId)}
                                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                              >
                                {expandedRows.has(app.applicationId) ? (
                                  <ChevronUpIcon className="w-5 h-5" />
                                ) : (
                                  <ChevronDownIcon className="w-5 h-5" />
                                )}
                              </button>
                            </div>

                            {/* Expanded History - Mobile */}

                            {expandedRows.has(app.applicationId) && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                {/* Stacked Layout on Mobile, Side-by-Side on Tablet */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  {/* History with inline comments */}
                                  <div>
                                    <BonusApplicationHistory
                                      historyItems={convertToHistoryItems(app)}
                                      comments={app.comments || []}
                                      historyAccordionExpanded={false}
                                      onAccordionToggle={() => { }}
                                      showAccordion={false}
                                      showTitle={true}
                                    />
                                  </div>

                                  {/* Standalone comments */}
                                  <div>
                                    <CommentComponent
                                      comment=""
                                      commentList={app.comments || []}
                                      commentsAccordionExpanded={false}
                                      onCommentChange={() => { }}
                                      onAddComment={() => { }}
                                      onAccordionToggle={() => { }}
                                      showAccordion={false}
                                      showTitle={true}
                                      readOnly={true}
                                      filterByHistoryId={null}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                        <div className="flex-1 flex justify-between sm:hidden">
                          <button
                            onClick={() => handlePageChange(filters.page - 1)}
                            disabled={filters.page === 1}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => handlePageChange(filters.page + 1)}
                            disabled={filters.page === totalPages}
                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                          <div>
                            <p className="text-sm text-gray-700">
                              Showing <span className="font-medium">{(filters.page - 1) * filters.pageSize + 1}</span> to{' '}
                              <span className="font-medium">{Math.min(filters.page * filters.pageSize, totalCount)}</span>{' '}
                              of <span className="font-medium">{totalCount}</span> results
                            </p>
                          </div>
                          <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                              <button
                                onClick={() => handlePageChange(1)}
                                disabled={filters.page === 1}
                                className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                First
                              </button>
                              <button
                                onClick={() => handlePageChange(filters.page - 1)}
                                disabled={filters.page === 1}
                                className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Previous
                              </button>
                              {(() => {
                                const pages: (number | string)[] = [];
                                const delta = 2;
                                for (let i = 1; i <= totalPages; i++) {
                                  if (i === 1 || i === totalPages || (i >= filters.page - delta && i <= filters.page + delta)) {
                                    pages.push(i);
                                  } else if (pages[pages.length - 1] !== '...') {
                                    pages.push('...');
                                  }
                                }
                                return pages.map((page, index) => {
                                  if (page === '...') {
                                    return (
                                      <span key={`ellipsis-${index}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                        ...
                                      </span>
                                    );
                                  }
                                  return (
                                    <button
                                      key={page}
                                      onClick={() => handlePageChange(page as number)}
                                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === filters.page
                                        ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                        }`}
                                    >
                                      {page}
                                    </button>
                                  );
                                });
                              })()}
                              <button
                                onClick={() => handlePageChange(filters.page + 1)}
                                disabled={filters.page === totalPages}
                                className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Next
                              </button>
                              <button
                                onClick={() => handlePageChange(totalPages)}
                                disabled={filters.page === totalPages}
                                className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Last
                              </button>
                            </nav>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Dialogs */}
      <ViewApplicationDialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        application={selectedApplication}
      />

      <UpdateReviewerDialog
        open={reviewerDialogOpen}
        onClose={handleCloseReviewerDialog}
        application={selectedApplication}
      />

      <UpdateStatusDialog
        open={statusDialogOpen}
        onClose={handleCloseStatusDialog}
        application={selectedApplication}
      />

    </div>
  );
};

export default Applications;